import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { UserContext } from "../../../UserContext";
import toast from "react-hot-toast";
import { FaPlus, FaTags, FaTrash } from "react-icons/fa";
import "./style.css";

const Types = () => {
  const [t, i18n] = useTranslation();
  const { token, userData } = useContext(UserContext);
  const [types, setTypes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const farmId = userData.Employee?.farmId;
  const tenantId = userData.Employee?.tenantId;

  // Fetch inventory types by farmId
  useEffect(() => {
    const fetchTypes = async () => {
      if (!farmId) return;
      setLoading(true);
      try {
        const response = await axios.get(
          `api/inventoryType/findByFarmId/${farmId}`,
          {
            headers: {
              Authorization: `${token}`,
            },
          }
        );
        setTypes(response.data.items || []);
      } catch (error) {
        console.error("Error fetching types:", error);
        toast.error(t("Failed to load inventory types."));
      } finally {
        setLoading(false);
      }
    };

    fetchTypes();
  }, [farmId, token, t]);

  // Add Type
  const handleSave = async () => {
    if (!name) {
      toast.error(t("Name is required"));
      return;
    }

    const requestBody = {
      nameAr: name,
      nameEn: name,
      tenantId,
      farmId,
    };

    const promise = new Promise(async (resolve, reject) => {
      try {
        const response = await axios.post(`api/inventoryType`, requestBody, {
          headers: {
            Authorization: `${token}`,
          },
        });
        setTypes([...types, response.data.items[0]]);
        setIsModalOpen(false);
        setName("");
        resolve(t("Type added successfully!"));
      } catch (error) {
        console.error("Error saving type:", error);
        reject(t("Failed to add type."));
      }
    });

    toast.promise(promise, {
      loading: t("Saving..."),
      success: (message) => message,
      error: (message) => message,
    });
  };

  // Open Delete Modal
  const openDeleteModal = (type) => {
    setTypeToDelete(type);
    setIsDeleteModalOpen(true);
  };

  // Delete Type
  const handleDelete = async () => {
    if (!typeToDelete) return;

    const promise = new Promise(async (resolve, reject) => {
      try {
        await axios.delete(`api/inventoryType/${typeToDelete.id}`, {
          headers: {
            Authorization: `${token}`,
          },
        });
        setTypes(types.filter(type => type.id !== typeToDelete.id));
        setIsDeleteModalOpen(false);
        setTypeToDelete(null);
        resolve(t("Type deleted successfully!"));
      } catch (error) {
        console.error("Error deleting type:", error);
        reject(t("Failed to delete type."));
      }
    });

    toast.promise(promise, {
      loading: t("Deleting..."),
      success: (message) => message,
      error: (message) => message,
    });
  };

  // Helper function to get display name
  const getTypeDisplayName = (type) => {
    if (!type) return "";
    return i18n.language === "ar" 
      ? type.nameAr || type.nameEn 
      : type.nameEn || type.nameAr;
  };

  if (loading) {
    return (
      <div className="types-container">
        <div className="loading-spinner">{t("Loading...")}</div>
      </div>
    );
  }

  return (
    <div className="types-container">
      {/* Header */}
      <header className="view-header">
        <div className="header-left-content">
          <div>
            <h1 className="view-header-title">{t("Inventory Types")}</h1>
            <p className="view-header-subtitle">
              {types.length} {t("types available")}
            </p>
          </div>
        </div>
        <button
          className="button-primary"
          onClick={() => setIsModalOpen(true)}
        >
          <FaPlus /> {t("Add Type")}
        </button>
      </header>

      {/* Types Grid */}
      <div className="types-grid">
        {types.length > 0 ? (
          types.map((type) => (
            <div key={type.id} className="type-card">
              <div className="type-card-content">
                <div className="type-icon">
                  <FaTags />
                </div>
                <div className="type-info">
                  <h3 className="type-name">{getTypeDisplayName(type)}</h3>
                  <span className="type-label">{t("Type")}</span>
                </div>
                <button
                  className="type-delete-button"
                  onClick={() => openDeleteModal(type)}
                  title={t("Delete")}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-data-message">
            <FaTags className="empty-icon" />
            <p>{t("No types found.")}</p>
            <p className="help-text">
              {t("Add your first type to categorize inventory items.")}
            </p>
          </div>
        )}
      </div>

      {/* Modal for Adding Types */}
      {isModalOpen && (
        <div className="modal-container">
          <div className="modal-box">
            <h2 className="modal-title">{t("Add New Type")}</h2>
            <div className="modal-input">
              <label htmlFor="typeName">{t("Name")}*</label>
              <input
                id="typeName"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("Enter type name")}
                required
              />
            </div>
            <div className="modal-buttons">
              <button 
                className="button-secondary" 
                onClick={() => {
                  setIsModalOpen(false);
                  setName("");
                }}
              >
                {t("Cancel")}
              </button>
              <button className="button-primary" onClick={handleSave}>
                {t("Save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && typeToDelete && (
        <div className="modal-container">
          <div className="modal-box type-delete-modal">
            <h2 className="modal-title">{t("Delete Type")}</h2>
            <p className="type-delete-message">
              {t("Are you sure you want to delete this type?")}
            </p>
            <div className="type-delete-item">
              <FaTags className="type-delete-icon" />
              <span className="type-delete-name">
                {getTypeDisplayName(typeToDelete)}
              </span>
            </div>
            <p className="type-delete-warning">
              {t("This action cannot be undone.")}
            </p>
            <div className="modal-buttons">
              <button 
                className="button-secondary" 
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setTypeToDelete(null);
                }}
              >
                {t("Cancel")}
              </button>
              <button 
                className="button-danger" 
                onClick={handleDelete}
              >
                {t("Delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Types;
